import { RiCompass4Line, Stack } from 'component-library';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';

import { ConservationExplainerWell, NCCardSectionHeader } from '@/components/NCCard/NCCard';
import { useGetConservationBenchmarks } from '@/pages/shared/projects/pages/project/pages/conservation/BU/hooks/useGetConservationBenchmarks';
import { useGetExplainerFallbackText } from '@/pages/shared/projects/pages/project/pages/conservation/BU/hooks/useGetExplainerFallbackText';
import { useGetTrendLabel } from '@/pages/shared/projects/pages/project/pages/conservation/BU/hooks/useGetTrendLabel';
import { getBenchmarkForConservationFact } from '@/pages/shared/projects/pages/project/pages/conservation/BU/utils/getBenchmarkForConservationFact';
import {
  FactCapsule,
  FactCapsuleTrend,
} from '@/pages/shared/projects/pages/project/pages/conservation/shared/components/FactCapsule';
import { useFact } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useFact';
import { useLatestAnalysisDatestamp } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useLatestAnalysisDatestamp';

const FACTS = {
  baseline: 'r1_conservation_threats_i_baseline',
  latest: 'r1_conservation_threats_i',
  change: 'r1_conservation_habitat_intactness_change_percentage',
} as const;

export const StatsTile = () => {
  const { t } = useTranslation();

  const currentYear = useFact<number>('r1_conservation_current_year');
  const baselineYear = useFact<number>('r1_conservation_habitat_intactness_baseline_year');
  const projectBaseline = useFact<number>(FACTS.baseline);
  const projectAverage = useFact<number>(FACTS.latest);
  const changePercentage = useFact<number>(FACTS.change).value;

  const hiDataRange: [number, number] = useFact<[number, number]>('r1_conservation_habitat_intactness_data_range')
    ?.value ?? [0, 100];
  const hiRangeUpperBound = hiDataRange?.[1];
  const benchmarks = useGetConservationBenchmarks(hiRangeUpperBound);
  const projectBenchmark = getBenchmarkForConservationFact({
    fact: projectAverage,
    benchmarks,
  });

  const trendLabel = useGetTrendLabel({
    trendChangeFact: FACTS.change,
  });

  const customExplainerText = useFact<string>('r1_conservation_habitat_intactness_historic_explainer');

  const { benchmarkText, trendText } = useGetExplainerFallbackText({
    benchmark: {
      label: projectBenchmark,
      value: projectAverage.value,
    },
    trend: {
      label: trendLabel,
      value: changePercentage,
    },
    indicator: 'habitatIntactness',
  });

  const fallbackExplainerText =
    benchmarkText && trendText ? `${benchmarkText}\n\n${trendText}` : benchmarkText || trendText || null;

  const latestAnalysisDatestamp = useLatestAnalysisDatestamp(
    projectAverage?.measured_at ? { facts: projectAverage } : undefined,
  );

  return (
    <Stack spacing={10} className='px-6' data-testid='habitat-intactness-stats-tile'>
      <Stack spacing={6}>
        <NCCardSectionHeader
          title={t('shared.ncaDetail.habitatIntactness.evolutionBU.statsHeading')}
          popover={{
            title: t('shared.ncaDetail.habitatIntactness.evolutionBU.explainers.habitatIntactness.title'),
            body: t('shared.ncaDetail.habitatIntactness.evolutionBU.explainers.habitatIntactness.body'),
          }}
          supplementaryInfo={latestAnalysisDatestamp}
        />
        <Stack spacing={16} className='flex-wrap md:flex-row'>
          <FactCapsule
            label={t('shared.ncaDetail.habitatIntactness.evolutionBU.baselinePrevFiveYears')}
            fact={projectBaseline}
            capsuleIconProps={{ icon: <RiCompass4Line />, variant: 'default' }}
            infoPopoverProps={{
              title: t('shared.ncaDetail.habitatIntactness.evolutionBU.explainers.baselineValue.title'),
              body: t('shared.ncaDetail.habitatIntactness.evolutionBU.explainers.baselineValue.body', {
                year: baselineYear.value,
              }),
              popoverTriggerProps: {
                'data-testid': 'habitat-intactness-baseline-value-popover',
              },
            }}
          />
          <FactCapsule
            label={t('shared.ncaDetail.habitatIntactness.evolutionBU.avgValue', {
              year: currentYear.value,
            })}
            fact={projectAverage}
            trend={<FactCapsuleTrend trend={changePercentage} />}
            capsuleIconProps={{ icon: <RiCompass4Line />, variant: 'default' }}
            infoPopoverProps={{
              title: t('shared.ncaDetail.habitatIntactness.evolutionBU.explainers.averageValue.title', {
                year: currentYear.value,
              }),
              body: t('shared.ncaDetail.habitatIntactness.evolutionBU.explainers.averageValue.body', {
                year: currentYear.value,
              }),
              popoverTriggerProps: {
                'data-testid': 'habitat-intactness-latest-analysis-popover',
              },
            }}
          />
        </Stack>
      </Stack>

      <ConservationExplainerWell
        benchmark={projectBenchmark}
        trendLabel={trendLabel}
        title={t('global.whatDoesThisMean')}
        body={
          customExplainerText?.value ? (
            <div
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(customExplainerText.value),
              }}
            />
          ) : (
            <span className='whitespace-pre-wrap'>{fallbackExplainerText}</span>
          )
        }
      />
    </Stack>
  );
};
